// src/pages/retail-shops/StudentDirectory.jsx
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import {
  Box, Typography, Grid, Button, Chip, IconButton, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  FormControl, InputLabel, Select, LinearProgress, Alert, Snackbar,
  TablePagination, InputAdornment, CircularProgress, Fade, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Avatar, alpha, Divider, Stack, Card, CardContent, Skeleton,
  Badge,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  CheckCircle as ActiveIcon,
  Block as InactiveIcon,
  AccountBalanceWallet as WalletIcon,
  History as HistoryIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Badge as BadgeIcon,
} from '@mui/icons-material';

import '../../styles/Waiters.css';
import Sidebar from '../../components/finance/Sidebar';
import Header from '../../components/finance/Header';
import Footer from '../../components/finance/Footer';
import { ACCE_BRAND, glossyButton } from '../../theme/colors';

// ════════════════════════════════════════════
// API
// ════════════════════════════════════════════
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000';
const API_PREFIX   = process.env.REACT_APP_API_PREFIX   || '/api';

const api = axios.create({
  baseURL: `${API_BASE_URL}${API_PREFIX}`,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const extractError = (err) => {
  if (!err) return 'Request failed';
  if (err.code === 'ECONNABORTED') return 'Request timed out. Please try again.';
  if (err.message === 'Network Error') return 'Network error — is the server running?';
  const res = err.response;
  if (!res) return err.message || 'Request failed';
  const d = res.data;
  if (typeof d === 'string') {
    const t = d.match(/<title>([^<]+)<\/title>/i);
    if (t) return t[1].trim();
    return d.slice(0, 200) || `Request failed (${res.status})`;
  }
  if (d && typeof d === 'object') {
    if (d.errors) {
      const first = Object.values(d.errors)[0];
      return Array.isArray(first) ? first[0] : String(first);
    }
    if (d.message) return d.message;
    if (d.error) return String(d.error);
  }
  return `Request failed (HTTP ${res.status})`;
};

const apiStudents = async (params = {}) => {
  try {
    const { data } = await api.get('/users/students', { params });
    return { success: true, ...data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

const apiStudentStats = async () => {
  try {
    const { data } = await api.get('/users/students/stats');
    return { success: true, stats: data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

const apiClasses = async () => {
  try {
    const { data } = await api.get('/classes');
    return { success: true, classes: data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

const apiWalletDetail = async (staffId, params = {}) => {
  try {
    const { data } = await api.get(`/tuckshop/wallets/${staffId}`, { params });
    return { success: true, ...data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

// ════════════════════════════════════════════
// UTILITIES
// ════════════════════════════════════════════
const formatCurrency = (v) => {
  if (v === null || v === undefined || v === '') return '₦0.00';
  return `₦${Number(v).toLocaleString('en-NG', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
};

const formatDateTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const initials = (first, other) => {
  const f = (first || '').trim();
  const o = (other || '').trim();
  const a = f.charAt(0) || '?';
  const b = o.charAt(0) || '';
  return (a + b).toUpperCase();
};

const fullName = (u) => {
  if (!u) return '';
  return `${u.first_name || ''} ${u.other_names || ''}`.trim();
};

// ════════════════════════════════════════════
// SUB-COMPONENTS
// ════════════════════════════════════════════
const StatCard = React.memo(({ label, value, sub, icon, color }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: '14px',
      border: `1px solid ${alpha(color, 0.15)}`,
      bgcolor: '#fff',
      height: '100%',
      position: 'relative',
      overflow: 'hidden',
      transition: 'all 0.25s ease',
      '&:hover': {
        transform: 'translateY(-3px)',
        boxShadow: `0 12px 30px ${alpha(color, 0.12)}`,
        borderColor: alpha(color, 0.4),
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 0, top: 0, bottom: 0, width: 4,
        bgcolor: color,
      },
    }}
  >
    <CardContent sx={{ p: 2, pl: 2.4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="caption" sx={{
            color: '#64748B', fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '0.6px', fontSize: '0.65rem',
            fontFamily: '"Inter", sans-serif', display: 'block', mb: 0.5,
          }}>
            {label}
          </Typography>
          <Typography variant="h6" fontWeight={800} sx={{
            fontSize: { xs: '1.05rem', sm: '1.3rem' },
            color: '#0F172A', lineHeight: 1.15,
            fontFamily: '"Inter", sans-serif', letterSpacing: '-0.5px',
          }}>
            {value}
          </Typography>
          {sub && (
            <Typography variant="caption" sx={{
              color: '#64748B', fontWeight: 600, fontSize: '0.68rem',
              fontFamily: '"Inter", sans-serif', display: 'block', mt: 0.4,
            }}>
              {sub}
            </Typography>
          )}
        </Box>
        <Avatar sx={{
          bgcolor: alpha(color, 0.1), color,
          width: 40, height: 40, borderRadius: '10px',
        }}>
          {icon}
        </Avatar>
      </Box>
    </CardContent>
  </Card>
));
StatCard.displayName = 'StatCard';

// ════════════════════════════════════════════
// STUDENT DETAIL DIALOG
// ════════════════════════════════════════════
const StudentDetailDialog = ({ open, student, onClose, pushSnackbar }) => {
  const [wallet, setWallet] = useState(null);
  const [walletLoading, setWalletLoading] = useState(false);

  useEffect(() => {
    if (!open || !student) return;
    setWallet(null);
    setWalletLoading(true);
    apiWalletDetail(student.staff_id, { page: 1, page_size: 5 })
      .then((res) => {
        if (res.success) setWallet(res);
        // Silently ignore "no wallet" 404 — the student just has no wallet yet
      })
      .finally(() => setWalletLoading(false));
  }, [open, student]);

  if (!student) return null;

  const isActive = student.is_active;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <Avatar sx={{ bgcolor: alpha(ACCE_BRAND.orange, 0.12), color: ACCE_BRAND.orange }}>
            <PersonIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={800} fontFamily='"Inter", sans-serif'>
              Student Details
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              {student.staff_id}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Avatar + Name */}
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <Avatar
            src={student.avatar || undefined}
            sx={{
              width: 72, height: 72,
              bgcolor: ACCE_BRAND.orange,
              fontWeight: 800, fontSize: '1.5rem',
            }}
          >
            {initials(student.first_name, student.other_names)}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" fontWeight={800} sx={{ fontFamily: '"Inter", sans-serif' }}>
              {fullName(student)}
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
              <Chip size="small" label={student.department || 'No class'}
                sx={{ fontWeight: 700, fontSize: '0.65rem', bgcolor: alpha('#1976d2', 0.1), color: '#1565c0' }} />
              <Chip
                size="small"
                icon={isActive ? <ActiveIcon sx={{ fontSize: 12 }} /> : <InactiveIcon sx={{ fontSize: 12 }} />}
                label={isActive ? 'Active' : 'Inactive'}
                sx={{
                  fontWeight: 700, fontSize: '0.65rem',
                  bgcolor: alpha(isActive ? '#4caf50' : '#f44336', 0.1),
                  color: isActive ? '#2e7d32' : '#c62828',
                }}
              />
              <Chip size="small" label={student.gender || '—'}
                sx={{ fontWeight: 700, fontSize: '0.65rem', bgcolor: alpha('#7b1fa2', 0.1), color: '#6a1b9a' }} />
            </Stack>
          </Box>
        </Stack>

        <Divider sx={{ mb: 2 }} />

        {/* Contact info */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <EmailIcon sx={{ color: '#64748B', fontSize: 18 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block' }}>
                  Email
                </Typography>
                <Typography variant="body2" noWrap sx={{ fontFamily: '"Inter", sans-serif' }}>
                  {student.email || '—'}
                </Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <PhoneIcon sx={{ color: '#64748B', fontSize: 18 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block' }}>
                  Phone
                </Typography>
                <Typography variant="body2" noWrap sx={{ fontFamily: '"Inter", sans-serif' }}>
                  {student.phone || '—'}
                </Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <BadgeIcon sx={{ color: '#64748B', fontSize: 18 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block' }}>
                  Staff ID
                </Typography>
                <Typography variant="body2" noWrap sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                  {student.staff_id}
                </Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <HistoryIcon sx={{ color: '#64748B', fontSize: 18 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block' }}>
                  Last login
                </Typography>
                <Typography variant="body2" noWrap sx={{ fontFamily: '"Inter", sans-serif' }}>
                  {formatDateTime(student.last_login)}
                </Typography>
              </Box>
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Wallet snippet */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <WalletIcon sx={{ color: '#64748B', fontSize: 18 }} />
          <Typography variant="subtitle2" fontWeight={800} sx={{ fontFamily: '"Inter", sans-serif' }}>
            Wallet
          </Typography>
        </Stack>

        {walletLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">Loading wallet…</Typography>
          </Box>
        ) : wallet?.wallet ? (
          <Paper variant="outlined" sx={{
            p: 2, borderRadius: 2,
            borderColor: alpha(ACCE_BRAND.orange, 0.2),
            bgcolor: alpha(ACCE_BRAND.orange, 0.02),
          }}>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>Balance</Typography>
                <Typography variant="h6" fontWeight={800} sx={{
                  color: ACCE_BRAND.orange, fontFamily: '"Inter", sans-serif',
                }}>
                  {formatCurrency(wallet.wallet.balance)}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>Total In</Typography>
                <Typography fontWeight={800} sx={{
                  color: '#2e7d32', fontFamily: '"Inter", sans-serif', fontSize: '0.9rem',
                }}>
                  {formatCurrency(wallet.wallet.total_credited)}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>Total Out</Typography>
                <Typography fontWeight={800} sx={{
                  color: '#c62828', fontFamily: '"Inter", sans-serif', fontSize: '0.9rem',
                }}>
                  {formatCurrency(wallet.wallet.total_debited)}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>Status</Typography>
                <Chip
                  size="small"
                  label={wallet.wallet.is_locked ? 'Locked' : 'Active'}
                  sx={{
                    fontWeight: 700, fontSize: '0.65rem',
                    bgcolor: alpha(wallet.wallet.is_locked ? '#f44336' : '#4caf50', 0.1),
                    color: wallet.wallet.is_locked ? '#c62828' : '#2e7d32',
                  }}
                />
              </Grid>
            </Grid>
          </Paper>
        ) : (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            This student does not have a wallet yet.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 700 }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// ════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════
const StudentDirectory = () => {
  const [activeMenu, setActiveMenu] = useState('Students');

  // Data
  const [students, setStudents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // Stats
  const [stats, setStats] = useState(null);

  // Classes
  const [classes, setClasses] = useState([]);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Filters
  const [searchValue, setSearchValue] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'active' | 'inactive'
  const [sort, setSort] = useState('name');
  const [order, setOrder] = useState('asc');

  // Detail
  const [detailStudent, setDetailStudent] = useState(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const debounceRef = useRef(null);

  const pushSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);
  const closeSnackbar = useCallback(() => setSnackbar((s) => ({ ...s, open: false })), []);

  // Debounce search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchDebounced(searchValue.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchValue]);

  // Build query params
  const buildParams = useCallback(() => {
    const params = {
      page: page + 1,
      page_size: rowsPerPage,
      sort,
      order,
    };
    if (searchDebounced) params.search = searchDebounced;
    if (selectedClass) params.class = selectedClass;
    if (statusFilter === 'active')   params.is_active = true;
    if (statusFilter === 'inactive') params.is_active = false;
    return params;
  }, [page, rowsPerPage, searchDebounced, selectedClass, statusFilter, sort, order]);

  // ── Fetch students ──
  const fetchStudents = useCallback(async () => {
    setLoading(true);
    const res = await apiStudents(buildParams());
    setLoading(false);
    if (res.success) {
      setStudents(res.data || []);
      setTotal(res.total || 0);
    } else {
      pushSnackbar(res.error || 'Failed to load students', 'error');
      setStudents([]);
      setTotal(0);
    }
  }, [buildParams, pushSnackbar]);

  // ── Fetch stats ──
  const fetchStats = useCallback(async () => {
    const res = await apiStudentStats();
    if (res.success) setStats(res.stats);
  }, []);

  // ── Fetch class list ──
  const fetchClasses = useCallback(async () => {
    const res = await apiClasses();
    if (res.success) setClasses(res.classes || []);
  }, []);

  useEffect(() => { fetchStudents(); }, [fetchStudents]);
  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchClasses(); }, [fetchClasses]);

  // Reset all filters
  const resetFilters = useCallback(() => {
    setSearchValue('');
    setSearchDebounced('');
    setSelectedClass('');
    setStatusFilter('all');
    setSort('name');
    setOrder('asc');
    setPage(0);
  }, []);

  const handleRefresh = useCallback(() => {
    fetchStudents();
    fetchStats();
    pushSnackbar('Refreshed', 'info');
  }, [fetchStudents, fetchStats, pushSnackbar]);

  const activeFiltersCount = useMemo(() => {
    let n = 0;
    if (selectedClass) n++;
    if (statusFilter !== 'all') n++;
    if (sort !== 'name' || order !== 'asc') n++;
    return n;
  }, [selectedClass, statusFilter, sort, order]);

  return (
    <div className="epos-wrapper">
      <div className="epos-main-layout">
        <Sidebar activeMenu={activeMenu} setActiveMenu={setActiveMenu} />

        <div className="epos-main">
          <Header />

          <div className="epos-content" style={{ background: '#f5f7fa' }}>
            {/* HERO */}
            <div className="hero-card">
              <div>
                <div className="hero-title" style={{ color: ACCE_BRAND.orange, fontWeight: 800 }}>
                  🎓 Student Directory
                </div>
                <div className="hero-text-wrapper">
                  <div className="hero-tagline">
                    <span className="hero-tagline-icon">✦</span>
                    Browse, filter, and search students by class
                  </div>
                </div>
              </div>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Tooltip title="Refresh">
                  <IconButton
                    onClick={handleRefresh}
                    sx={{
                      bgcolor: alpha(ACCE_BRAND.orange, 0.1),
                      '&:hover': { bgcolor: alpha(ACCE_BRAND.orange, 0.2) },
                      borderRadius: 2,
                    }}
                  >
                    <RefreshIcon sx={{ color: ACCE_BRAND.orange }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </div>

            {/* STATS */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={6} md={3}>
                <StatCard
                  label="Total Students"
                  value={stats?.total_students ?? 0}
                  sub={`${stats?.recently_added ?? 0} added in last 30 days`}
                  icon={<SchoolIcon />}
                  color={ACCE_BRAND.orange}
                />
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <StatCard
                  label="Active"
                  value={stats?.active_students ?? 0}
                  sub={`${stats?.inactive_students ?? 0} inactive`}
                  icon={<ActiveIcon />}
                  color="#2e7d32"
                />
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <StatCard
                  label="With Wallets"
                  value={stats?.with_wallets ?? 0}
                  sub="Students with wallet accounts"
                  icon={<WalletIcon />}
                  color="#1976d2"
                />
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <StatCard
                  label="Classes"
                  value={stats?.by_class?.length ?? 0}
                  sub="Active class groups"
                  icon={<SchoolIcon />}
                  color="#7b1fa2"
                />
              </Grid>
            </Grid>

            {/* CLASS BREAKDOWN */}
            {stats?.by_class?.length > 0 && (
              <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1.5, fontFamily: '"Inter", sans-serif' }}>
                  Students per class · click to filter
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {stats.by_class.map((c) => {
                    const active = selectedClass === c.class;
                    return (
                      <Chip
                        key={c.class}
                        label={`${c.class} · ${c.count}`}
                        onClick={() => {
                          setSelectedClass(active ? '' : c.class);
                          setPage(0);
                        }}
                        sx={{
                          fontWeight: 800,
                          fontSize: '0.78rem',
                          height: 34,
                          cursor: 'pointer',
                          bgcolor: active ? ACCE_BRAND.orange : alpha(ACCE_BRAND.orange, 0.08),
                          color: active ? '#fff' : ACCE_BRAND.orange,
                          border: '1px solid',
                          borderColor: active ? ACCE_BRAND.orange : alpha(ACCE_BRAND.orange, 0.25),
                          transition: 'all 0.15s ease',
                          '&:hover': {
                            bgcolor: active ? ACCE_BRAND.orangeDark : alpha(ACCE_BRAND.orange, 0.15),
                          },
                        }}
                      />
                    );
                  })}
                  {selectedClass && (
                    <Chip
                      label="Clear class filter"
                      onDelete={() => { setSelectedClass(''); setPage(0); }}
                      sx={{
                        fontWeight: 700, fontSize: '0.75rem', height: 34,
                        bgcolor: alpha('#c62828', 0.08), color: '#c62828',
                        '& .MuiChip-deleteIcon': { color: '#c62828' },
                      }}
                    />
                  )}
                </Stack>
              </Paper>
            )}

            {/* FILTER BAR */}
            <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search by name, staff ID, email, or phone…"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  sx={{
                    flex: 1, minWidth: 240,
                    '& .MuiOutlinedInput-root': { borderRadius: 2 },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: searchValue && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchValue('')}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel sx={{ fontWeight: 700 }}>Class</InputLabel>
                  <Select
                    value={selectedClass}
                    label="Class"
                    onChange={(e) => { setSelectedClass(e.target.value); setPage(0); }}
                    sx={{ borderRadius: 2, fontWeight: 600 }}
                  >
                    <MenuItem value="" sx={{ fontWeight: 600 }}>All classes</MenuItem>
                    {classes.map((c) => (
                      <MenuItem key={c.value} value={c.value} sx={{ fontWeight: 600 }}>
                        {c.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel sx={{ fontWeight: 700 }}>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                    sx={{ borderRadius: 2, fontWeight: 600 }}
                  >
                    <MenuItem value="all" sx={{ fontWeight: 600 }}>All</MenuItem>
                    <MenuItem value="active" sx={{ fontWeight: 600 }}>Active</MenuItem>
                    <MenuItem value="inactive" sx={{ fontWeight: 600 }}>Inactive</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel sx={{ fontWeight: 700 }}>Sort by</InputLabel>
                  <Select
                    value={sort}
                    label="Sort by"
                    onChange={(e) => { setSort(e.target.value); setPage(0); }}
                    sx={{ borderRadius: 2, fontWeight: 600 }}
                  >
                    <MenuItem value="name" sx={{ fontWeight: 600 }}>Name</MenuItem>
                    <MenuItem value="staff_id" sx={{ fontWeight: 600 }}>Staff ID</MenuItem>
                    <MenuItem value="class" sx={{ fontWeight: 600 }}>Class</MenuItem>
                    <MenuItem value="created_at" sx={{ fontWeight: 600 }}>Recently added</MenuItem>
                  </Select>
                </FormControl>

                {activeFiltersCount > 0 && (
                  <Button
                    variant="text"
                    onClick={resetFilters}
                    startIcon={<ClearIcon />}
                    sx={{ textTransform: 'none', fontWeight: 700, color: '#c62828' }}
                  >
                    Clear all
                  </Button>
                )}
              </Stack>
            </Paper>

            {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

            {/* TABLE */}
            <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{
                p: 2, display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', flexWrap: 'wrap', gap: 1,
              }}>
                <Typography variant="subtitle2" fontWeight={800} sx={{ fontFamily: '"Inter", sans-serif' }}>
                  {total} student{total === 1 ? '' : 's'}
                  {selectedClass && ` in ${selectedClass}`}
                </Typography>
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: alpha(ACCE_BRAND.orange, 0.04) }}>
                      <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Student</TableCell>
                      <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Staff ID</TableCell>
                      <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Class</TableCell>
                      <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Gender</TableCell>
                      <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Contact</TableCell>
                      <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Last login</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading && students.length === 0 ? (
                      [...Array(6)].map((_, i) => (
                        <TableRow key={i}>
                          {[...Array(8)].map((__, j) => (
                            <TableCell key={j}><Skeleton variant="text" /></TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : students.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                          <SchoolIcon sx={{ fontSize: 60, color: 'text.disabled', opacity: 0.4, mb: 1 }} />
                          <Typography variant="h6" color="text.secondary" fontWeight={700} fontFamily='"Inter", sans-serif'>
                            No students found
                          </Typography>
                          <Typography variant="body2" color="text.secondary" fontFamily='"Inter", sans-serif'>
                            {selectedClass
                              ? `No students in ${selectedClass} match your filters.`
                              : 'Try adjusting your search or filters.'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      students.map((s) => (
                        <TableRow
                          key={s.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => setDetailStudent(s)}
                        >
                          <TableCell>
                            <Stack direction="row" alignItems="center" spacing={1.2}>
                              <Avatar
                                src={s.avatar || undefined}
                                sx={{
                                  bgcolor: alpha(ACCE_BRAND.orange, 0.15),
                                  color: ACCE_BRAND.orange,
                                  width: 36, height: 36,
                                  fontWeight: 800, fontSize: '0.78rem',
                                }}
                              >
                                {initials(s.first_name, s.other_names)}
                              </Avatar>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography fontWeight={700} sx={{
                                  fontFamily: '"Inter", sans-serif', fontSize: '0.85rem',
                                }}>
                                  {fullName(s)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" noWrap
                                  sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.72rem' }}>
                                  {s.email || '—'}
                                </Typography>
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={s.staff_id}
                              sx={{
                                fontFamily: 'monospace', fontWeight: 700, fontSize: '0.68rem',
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={s.department || '—'}
                              sx={{
                                fontWeight: 700, fontSize: '0.68rem',
                                bgcolor: alpha('#1976d2', 0.1), color: '#1565c0',
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8rem', color: '#475569', textTransform: 'capitalize' }}>
                            {s.gender || '—'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.72rem', color: '#64748B' }}>
                            <Typography variant="caption" sx={{ display: 'block', fontFamily: '"Inter", sans-serif' }}>
                              {s.phone || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              icon={s.is_active ? <ActiveIcon sx={{ fontSize: 12 }} /> : <InactiveIcon sx={{ fontSize: 12 }} />}
                              label={s.is_active ? 'Active' : 'Inactive'}
                              sx={{
                                fontWeight: 700, fontSize: '0.65rem',
                                bgcolor: alpha(s.is_active ? '#4caf50' : '#f44336', 0.1),
                                color: s.is_active ? '#2e7d32' : '#c62828',
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.72rem', color: '#64748B' }}>
                            {formatDateTime(s.last_login)}
                          </TableCell>
                          <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                            <Tooltip title="View details">
                              <IconButton size="small" onClick={() => setDetailStudent(s)}>
                                <HistoryIcon fontSize="small" sx={{ color: '#1976d2' }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={total}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              />
            </Paper>
          </div>
        </div>
      </div>
      <Footer />

      <StudentDetailDialog
        open={Boolean(detailStudent)}
        student={detailStudent}
        onClose={() => setDetailStudent(null)}
        pushSnackbar={pushSnackbar}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Fade}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', fontWeight: 700, fontFamily: '"Inter", sans-serif' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default StudentDirectory;